import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEmpresa } from "../../context/EmpresaContext";
import { getEmpresaBrand, login, loginEmpresaAdmin } from "../../services/sininApi";
import sininLogo from "../../assets/sinin-logo.svg";
import GlassSpinner from "../../components/GlassSpinner";

const SININ_DARK = "#05080a";
const SININ_TURQ = "#25f4df";

export default function LoginPlataforma() {
  const navigate       = useNavigate();
  const { empresaId }  = useParams();
  const empresaSlug    = String(empresaId || "").trim().toUpperCase();
  const { setEmpresa } = useEmpresa();

  const [dni, setDni]             = useState("");
  const [password, setPassword]   = useState("");
  const [showPass, setShowPass]   = useState(false);
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [empresaConfig, setEmpresaConfig] = useState(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(!!empresaSlug);

  useEffect(() => {
    if (!empresaSlug) {
      setEmpresaConfig(null);
      setLoadingEmpresa(false);
      return;
    }
    setLoadingEmpresa(true);
    getEmpresaBrand(empresaSlug.toLowerCase())
      .then(res => {
        if (res?.empresa) setEmpresaConfig(res.empresa);
        else setError(res?.error || "Empresa inexistente o inactiva.");
      })
      .catch(() => setError("No pudimos cargar los datos de la empresa."))
      .finally(() => setLoadingEmpresa(false));
  }, [empresaSlug]);

  const empresaLogo = empresaConfig?.logoUrl || sininLogo;

  const handleLogin = async () => {
    setError("");
    if (!dni || !password) return setError("Completá DNI y contraseña.");
    setLoading(true);
    try {
      const data = empresaSlug
        ? await loginEmpresaAdmin(empresaSlug, dni.trim(), password)
        : await login(dni.trim(), password);

      if (data.error) {
        setError(data.error);
        setLoading(false);
        return;
      }

      // Guardar token y datos de empresa en localStorage vía contexto
      localStorage.setItem("sinin_token", data.token || "");
      setEmpresa({
        empresaId:  data.empresaId,
        role:       data.role,
        loginType:  data.loginType || "0",
        nombre:     data.nombre    || data.empresaId,
        first_name: data.first_name || "",
        last_name:  data.last_name  || "",
        dni:        data.dni,
        eventos:    data.eventos || [],
        membresias: data.membresias || [],
        // colores — se pueden cargar aparte desde SININ_EMPRESAS si es necesario
        colorTicket: data.colorTicket || SININ_TURQ,
        colorFondo:  data.colorFondo  || "#1a1a1a",
        colorFuente: data.colorFuente || "#ffffff",
        colorDark:   data.colorDark   || "#333333",
        logoKey:     data.logoKey     || "",
        logoUrl:     empresaConfig?.logoUrl || data.logoUrl || "",
      });

      if (data.role === "superadmin")    navigate("/admin");
      else if (data.role === "users")    navigate("/cliente/eventos");
      else if (data.role === "scanner")  navigate("/scanner");
      else                               navigate("/vendedor/eventos");

    } catch {
      setError("Error de conexión. Intentá de nuevo.");
    }
    setLoading(false);
  };

  const handleKey = e => { if (e.key === "Enter") handleLogin(); };

  return (
    <div style={{ ...styles.page, ...(empresaConfig ? brandedPage(empresaConfig) : {}) }}>
      <div style={{ ...styles.card, color: empresaConfig?.colorFuente || "#fff" }}>
        {!empresaSlug && <img src={sininLogo} alt="SININ" style={styles.logoImg} />}
        {loadingEmpresa ? (
          <div style={{ padding: "8px 0 16px" }}>
            <GlassSpinner size={48} background="#071416" />
          </div>
        ) : empresaSlug && empresaConfig ? (
          <div style={styles.companyIdentity}>
            <img
              src={empresaLogo}
              alt={empresaConfig.nombre || empresaSlug}
              style={styles.companyLogo}
              onError={e => { e.currentTarget.src = sininLogo; }}
            />
            <div style={styles.companyName}>{empresaConfig.nombre || empresaSlug}</div>
          </div>
        ) : null}
        <h1 style={{ ...styles.title, color: empresaConfig?.colorFuente || "#fff" }}>PLATAFORMA ADMINISTRATIVA</h1>
        <p style={{ ...styles.sub, color: empresaConfig?.colorFuente || "rgba(255,255,255,.78)" }}>{empresaSlug ? `Acceso administrativo · ${empresaConfig?.nombre || empresaSlug}` : "Superadministración SININ"}</p>

        <label style={{ ...styles.label, color: empresaConfig?.colorFuente || "#e9ffff" }}>DNI</label>
        <input style={styles.input} value={dni}
          onChange={e => setDni(e.target.value)}
          onKeyDown={handleKey}
          placeholder="12345678" />

        <label style={{ ...styles.label, color: empresaConfig?.colorFuente || "#e9ffff" }}>Contraseña</label>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input style={{ ...styles.input, flex: 1 }}
            type={showPass ? "text" : "password"}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={handleKey} />
          <span style={styles.eye}
            onClick={() => setShowPass(p => !p)}>
            {showPass ? "🙈" : "👁️"}
          </span>
        </div>

        {error && <p style={styles.error}>{error}</p>}

        <button style={{ ...styles.btn, background: empresaConfig?.colorTicket || styles.btn.background, color: empresaConfig?.colorFuente || "#f4fffe", opacity: loading || loadingEmpresa ? 0.7 : 1 }}
          onClick={handleLogin} disabled={loading || loadingEmpresa || (!!empresaSlug && !empresaConfig)}>
          {loading ? "INGRESANDO..." : "INGRESAR"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page:  {
    background:
      "radial-gradient(circle at 16% 12%, rgba(37,244,223,.55), transparent 30%), radial-gradient(circle at 84% 86%, rgba(70,130,255,.24), transparent 34%), linear-gradient(135deg, #071416 0%, #05080a 58%, #10151a 100%)",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    boxSizing: "border-box",
  },
  card:  {
    background: "rgba(255,255,255,.12)",
    border: "1px solid rgba(255,255,255,.22)",
    borderRadius: 28,
    padding: 28,
    width: "100%",
    maxWidth: 390,
    boxShadow: "0 24px 70px rgba(0,0,0,.45)",
    backdropFilter: "blur(18px)",
  },
  logoImg: { display: "block", width: 220, maxWidth: "100%", margin: "0 auto 10px" },
  companyIdentity: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
    margin: "6px auto 14px", padding: "10px 14px", width: "fit-content", maxWidth: "100%",
    borderRadius: 22, background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.22)" },
  companyLogo: { width: 72, height: 72, objectFit: "contain", borderRadius: 16,
    boxSizing: "border-box", background: "transparent" },
  companyName: { color: "#fff", fontWeight: "900", fontSize: 18, letterSpacing: .7,
    textTransform: "uppercase", overflowWrap: "anywhere" },
  title: { textAlign: "center", color: "#fff", fontSize: 18, margin: "8px 0 4px", letterSpacing: 1.5 },
  sub: { textAlign: "center", color: "rgba(255,255,255,.78)", fontSize: 13, marginBottom: 24, letterSpacing: 1, textTransform: "uppercase" },
  label: { display: "block", fontWeight: "900", fontSize: 12, marginTop: 14, marginBottom: 6, color: "#e9ffff", letterSpacing: 1, textTransform: "uppercase" },
  input: {
    padding: "13px 15px",
    borderRadius: 18,
    width: "100%",
    border: "1px solid rgba(255,255,255,.34)",
    background: "rgba(255,255,255,.9)",
    color: SININ_DARK,
    fontSize: 15,
    boxSizing: "border-box",
    outline: "none",
  },
  eye: {
    marginLeft: 10,
    cursor: "pointer",
    fontSize: 18,
    width: 42,
    height: 42,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,.16)",
    border: "1px solid rgba(255,255,255,.22)",
  },
  btn:   {
    background: "linear-gradient(145deg, rgba(207,255,250,.62) 0%, rgba(37,244,223,.34) 46%, rgba(14,145,137,.28) 100%)",
    color: "#f4fffe",
    padding: 15,
    width: "100%",
    borderRadius: 25,
    border: "1px solid rgba(218,255,251,.72)",
    marginTop: 24,
    cursor: "pointer",
    fontWeight: "900",
    fontSize: 15,
    letterSpacing: 1,
    textShadow: "0 1px 3px rgba(0,45,42,.72)",
    boxShadow: "inset 0 2px 1px rgba(255,255,255,.62), inset 0 -5px 12px rgba(0,78,73,.22), 0 7px 0 rgba(6,91,86,.7), 0 15px 30px rgba(37,244,223,.3)",
    backdropFilter: "blur(14px) saturate(150%)",
    WebkitBackdropFilter: "blur(14px) saturate(150%)",
  },
  error: { color: "#fff", background: "rgba(255,48,91,.86)", borderRadius: 16, padding: 10, marginTop: 12, textAlign: "center", fontSize: 13, fontWeight: 800 },
};

const brandedPage = empresa => ({
  background: `radial-gradient(circle at 15% 12%, ${empresa.colorTicket || SININ_TURQ}88, transparent 34%), linear-gradient(145deg, ${empresa.colorFondo || "#1a1a1a"}, ${empresa.colorDark || empresa.colorFondo || SININ_DARK})`,
});
